// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Badge,
  Alert,
  AlertDescription,
  Separator,
  Switch,
} from '@roger/ui-components';
import {
  Loader2,
  ArrowLeft,
  Calendar,
  User,
  PlayCircle,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  History,
} from 'lucide-react';
import {
  QAVisit,
  startQAAnalysis,
  getLatestQAStatus,
  getAllVisitQAData,
} from '../services/rogerDocApi';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import PatientInformationSection from './PatientInformationSection';
import MedicalDataModal from './MedicalDataModal';
import CardTitleWithIcon from './CardTitleWithIcon';
import ClickableTextWithModals from './ClickableTextWithModals';
import {
  QA_STATUS,
  QADataResponse,
  VisitDataResponse,
} from '../types/visitData.types';
import {
  getQAStatusDisplayLabel,
  getQAStatusBadgeStyles,
  isQAStatusInProgress,
  isQAStatusCompleted,
} from '../utils/qaStatusUtils';

interface QAVisitDetailsProps {
  visit: QAVisit;
  visitData: VisitDataResponse;
  loading: boolean;
  onBack: () => void;
  onOpenPdf: (pdfKey: string) => void;
  formatDate: (dateString: string) => string;
}

const hasValidQAData = (qaData: QAData | null): boolean => {
  return !!(qaData && qaData.qa_id && qaData.status && qaData.created_at);
};

const QAVisitDetails: React.FC<QAVisitDetailsProps> = ({
  visit,
  visitData,
  loading,
  onBack,
  onOpenPdf,
  formatDate,
}) => {
  const { showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const [loadingQA, setLoadingQA] = useState(false);
  const [startingQA, setStartingQA] = useState(false);
  const [latestQAStatus, setLatestQAStatus] = useState<QADataResponse>();
  const [allQAData, setAllQAData] = useState<QADataResponse[]>([]);
  const [selectedQaId, setSelectedQaId] = useState<string>();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    transcript: false,
    hnpSummary: false,
    formAnswers: false,
    medicalData: false,
    analysisMetrics: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    dataType: string;
    data: unknown;
  } | null>(null);
  const [expertViewEnabled, setExpertViewEnabled] = useState(false);

  // Fetch QA data
  const fetchQAData = useCallback(async () => {
    if (!visit.visit_id) return;

    setLoadingQA(true);
    try {
      const [latestStatus, allData] = await Promise.all([
        getLatestQAStatus(visit.visit_id),
        getAllVisitQAData(visit.visit_id),
      ]);
      setLatestQAStatus((latestStatus as QAData) || null);
      setAllQAData((allData as QAData[]) || []);

      // Auto-select the most recent QA if not already selected
      if (!selectedQaId && allData && (allData as QAData[]).length > 0) {
        setSelectedQaId((allData as QAData[])[0].qa_id);
      }
    } catch (error) {
      console.error('Error fetching QA data:', error);
    } finally {
      setLoadingQA(false);
    }
  }, [visit.visit_id, selectedQaId]);

  // Initial fetch
  useEffect(() => {
    if (visit.visit_id && !loading) {
      fetchQAData();
    }
  }, [visit.visit_id, loading, fetchQAData]);

  // Polling for in-progress QA
  useEffect(() => {
    if (
      !hasValidQAData(latestQAStatus) ||
      !isQAStatusInProgress(latestQAStatus.status)
    ) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchQAData();
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [latestQAStatus, fetchQAData]);

  const handleStartQA = async () => {
    setStartingQA(true);
    try {
      await startQAAnalysis(visit.visit_id, true);
      showSuccess('QA analysis started successfully');
      // Wait 3 seconds before fetching to give the backend time to initialize
      setTimeout(() => {
        fetchQAData();
      }, 3000);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to start QA analysis';
      showError(message);
    } finally {
      setStartingQA(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleOpenModal = (dataType: string, data: unknown) => {
    const titleMap: Record<string, string> = {
      home_health_orders: 'Home Health Order',
      face_to_face_visits: 'Face-to-Face Visit Summary',
      history_and_physical: 'History & Physical',
      demographics: 'Demographics',
      past_visit_diagnoses: 'Past Visit Diagnoses',
      narrative: 'Visit Narrative',
      medications: 'Medications',
      wounds: 'Wound Care',
      medical_form: 'Full Visit Data',
      visit_transcript: 'Visit Transcript',
      hnp_summary: 'HnP Summary',
      authorized_diagnoses: 'Authorized Diagnoses',
    };

    // Special handling for data types from selectedQAData or visitData
    let modalContent = data;
    if (dataType === 'visit_transcript' && selectedQAData) {
      modalContent = selectedQAData.bulleted_transcript_chunks || data;
    } else if (dataType === 'hnp_summary' && visitData) {
      modalContent = visitData.hnp_summary || data;
    }

    setModalData({
      title: titleMap[dataType] || 'Medical Data',
      dataType,
      data: modalContent,
    });
    setModalOpen(true);
  };

  // Get corrections count
  const getCorrectionsCount = (qa: QAData) => {
    return (qa.qa_corrections_by_rule || []).length;
  };

  // Format time in seconds to human readable format
  const formatTime = (seconds: number): string => {
    const roundedSeconds = Math.round(seconds);
    if (roundedSeconds < 60) {
      return `${roundedSeconds}s`;
    }
    const minutes = Math.floor(roundedSeconds / 60);
    const remainingSeconds = roundedSeconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  // Format date to a readable format
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Get total time from timings object
  const getTotalTime = (qa: QAData): number => {
    if (!qa.timings || Object.keys(qa.timings).length === 0) return 0;
    const totalTiming = qa.timings['total'];
    // Convert milliseconds to seconds for display
    return totalTiming && totalTiming.duration_ms
      ? totalTiming.duration_ms / 1000
      : 0;
  };

  // Get the currently selected QA data
  const selectedQAData = selectedQaId
    ? allQAData.find(qa => qa.qa_id === selectedQaId) || latestQAStatus
    : latestQAStatus;

  return (
    <>
      <Button variant='outline' onClick={onBack} className='mb-6'>
        <ArrowLeft className='w-4 h-4' />
        Back to Visit List
      </Button>
      {/* Visit Header */}
      <Card className='mb-6 border-primary-200 bg-primary-50'>
        <CardContent>
          <div className='flex items-center justify-between'>
            <div className='space-y-2'>
              <div className='flex items-baseline gap-4'>
                <h2 className='text-xl font-bold text-primary'>
                  {visit.patient_first_name} {visit.patient_last_name}
                </h2>
                {visit.patient_id && (
                  <span className='text-xs text-gray-500 font-medium'>
                    MRN: {visit.patient_id}
                  </span>
                )}
              </div>
            </div>

            <div className='flex items-center gap-6'>
              <div className='flex items-center gap-2 text-gray-700'>
                <Calendar className='w-4 h-4 text-gray-500' />
                <span className='text-sm font-medium'>
                  {formatDate(visit.visit_date)}
                </span>
              </div>
              <div className='flex items-center gap-2 text-gray-700'>
                <User className='w-4 h-4 text-gray-500' />
                <span className='text-sm font-medium'>
                  {visit.nurse_username}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <Badge className='text-sm font-medium'>
                  {visit.visit_type}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Patient Information Section */}
      {!loading &&
        visitData &&
        hasValidQAData(selectedQAData) &&
        isQAStatusCompleted(selectedQAData.status) &&
        selectedQAData && (
          <PatientInformationSection
            medicalData={selectedQAData.medical_data}
            onOpenModal={handleOpenModal}
            handleOpenPdf={onOpenPdf}
            bulletedTranscriptChunks={selectedQAData.bulleted_transcript_chunks}
          />
        )}
      {/* Expert View Toggle - Only visible with debug:access permission and when QA is complete */}
      {!loading &&
        hasValidQAData(selectedQAData) &&
        isQAStatusCompleted(selectedQAData.status) &&
        hasPermission('debug:access') && (
          <div className='mb-6 flex items-center justify-end gap-3'>
            <Switch
              id='expert-view'
              checked={expertViewEnabled}
              onCheckedChange={setExpertViewEnabled}
            />
            <label
              htmlFor='expert-view'
              className='text-sm font-medium text-gray-700 cursor-pointer'
            >
              Expert view
            </label>
          </div>
        )}
      {/* Loading State */}
      {loading && (
        <div className='flex justify-center items-center py-12'>
          <div className='flex items-center gap-3 text-gray-500'>
            <Loader2 className='h-6 w-6 animate-spin text-blue-600' />
            <span>Loading visit details...</span>
          </div>
        </div>
      )}
      {/* QA Analysis Section */}
      {!loading && visitData && (
        <>
          {/* QA Analysis History - Show when there are multiple analyses */}
          {allQAData && allQAData.length > 1 && (
            <Card className='mb-6'>
              <CardContent>
                <CardTitleWithIcon
                  icon={History}
                  title={`QA Analysis History (${allQAData.length})`}
                  subtitle='View previous analysis runs'
                />
                <div className='overflow-x-auto'>
                  <div className='flex gap-3 pb-2'>
                    {allQAData.map((qaDoc, index) => {
                      const isSelected = qaDoc.qa_id === selectedQaId;
                      const totalCorrections = (
                        qaDoc.qa_corrections_by_rule || []
                      ).length;
                      const totalTime = getTotalTime(qaDoc);

                      return (
                        <div
                          key={qaDoc.qa_id}
                          className={`group min-w-[280px] p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-primary-400'
                          }`}
                          onClick={() => setSelectedQaId(qaDoc.qa_id)}
                        >
                          <div className='space-y-3'>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-2 flex-wrap'>
                                <Badge
                                  className={`${getQAStatusBadgeStyles(qaDoc.status)} border`}
                                >
                                  {getQAStatusDisplayLabel(qaDoc.status)}
                                </Badge>
                              </div>
                              {index === 0 && (
                                <Badge className='text-xs bg-blue-100 text-blue-800 border-blue-300'>
                                  LATEST
                                </Badge>
                              )}
                            </div>

                            <div className='flex items-center gap-2'>
                              <Clock className='w-3.5 h-3.5 text-gray-500 flex-shrink-0' />
                              <div className='text-xs text-gray-700 font-medium'>
                                {formatDateTime(qaDoc.created_at)}
                              </div>
                            </div>

                            {qaDoc.status === QA_STATUS.COMPLETED && (
                              <div className='flex flex-wrap items-center gap-2'>
                                <div className='flex items-center gap-1.5 px-2 py-1 bg-purple-50 border border-purple-200 rounded-md'>
                                  <AlertCircle className='w-3 h-3 text-purple-700' />
                                  <span className='text-xs font-semibold text-purple-700'>
                                    {totalCorrections} Correction
                                    {totalCorrections !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                {totalTime > 0 && (
                                  <div className='flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md'>
                                    <Clock className='w-3 h-3 text-gray-600' />
                                    <span className='text-xs font-semibold text-gray-700'>
                                      {formatTime(totalTime)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className='mb-6'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitleWithIcon
                  icon={CheckCircle}
                  title='QA Analysis'
                  subtitle='Automated quality assurance review'
                />
                {hasValidQAData(selectedQAData) && (
                  <div className='flex items-center gap-4'>
                    <div className='flex items-center gap-2'>
                      <Badge
                        className={`${getQAStatusBadgeStyles(selectedQAData.status)} border`}
                      >
                        {getQAStatusDisplayLabel(selectedQAData.status)}
                      </Badge>
                      <span className='text-sm text-gray-600'>
                        {formatDateTime(selectedQAData.created_at)}
                      </span>
                    </div>
                    {selectedQAData.status === QA_STATUS.COMPLETED && (
                      <Button onClick={handleStartQA} disabled={startingQA}>
                        <RefreshCw
                          className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                        />
                        Rerun Analysis
                      </Button>
                    )}
                    {selectedQAData.status === QA_STATUS.FAILED && (
                      <Button
                        variant='destructive'
                        onClick={handleStartQA}
                        disabled={startingQA}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                        />
                        Retry Analysis
                      </Button>
                    )}
                    {isQAStatusInProgress(selectedQAData.status) && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={fetchQAData}
                        disabled={loadingQA}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${loadingQA ? 'animate-spin' : ''}`}
                        />
                        Refresh
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!hasValidQAData(selectedQAData) && !loadingQA && (
                <div className='py-8'>
                  <div className='text-center'>
                    <AlertCircle className='w-12 h-12 mx-auto mb-4 text-gray-400' />
                    <p className='text-gray-600 mb-4 font-medium'>
                      No analysis has been performed yet
                    </p>
                  </div>
                  <div className='flex justify-end'>
                    <Button
                      onClick={handleStartQA}
                      size='lg'
                      disabled={startingQA}
                    >
                      <PlayCircle
                        className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                      />
                      Start QA Analysis
                    </Button>
                  </div>
                </div>
              )}

              {hasValidQAData(selectedQAData) &&
                isQAStatusInProgress(selectedQAData.status) && (
                  <Alert className='bg-blue-50 border-blue-200 mb-4'>
                    <AlertDescription className='text-blue-800 flex items-center gap-2'>
                      <RefreshCw className={`w-4 h-4 animate-spin`} />
                      QA analysis in progress... Auto-refreshing every 3
                      seconds.
                    </AlertDescription>
                  </Alert>
                )}

              {/* QA Metadata Section */}
              {hasValidQAData(selectedQAData) && (
                <div className='space-y-4 mb-6'>
                  {/* Analysis Type */}
                  {selectedQAData.use_structured_qa_rules !== undefined && (
                    <div>
                      <div className='text-sm font-medium text-gray-700'>
                        Analysis Type
                      </div>
                      <div className='mt-1'>
                        <Badge
                          variant='secondary'
                          className='bg-green-100 text-green-800 border-green-200'
                        >
                          {selectedQAData.use_structured_qa_rules
                            ? 'STRUCTURED RULES'
                            : 'AI ANALYSIS'}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {/* Analysis Metrics (Collapsible) */}
                  {((selectedQAData.timings &&
                    Object.keys(selectedQAData.timings).length > 0) ||
                    (selectedQAData.cost &&
                      selectedQAData.cost.length > 0)) && (
                    <div>
                      <button
                        onClick={() => toggleSection('analysisMetrics')}
                        className='flex items-center gap-2 w-full text-left font-medium text-sm hover:text-blue-600'
                      >
                        {expandedSections.analysisMetrics ? (
                          <ChevronUp className='w-4 h-4' />
                        ) : (
                          <ChevronDown className='w-4 h-4' />
                        )}
                        Analysis Metrics
                      </button>
                      {expandedSections.analysisMetrics && (
                        <div className='mt-3 p-3 bg-gray-50 rounded-md border'>
                          <div className='grid grid-cols-1 gap-6'>
                            {/* Analysis Time */}
                            {selectedQAData.timings &&
                              Object.keys(selectedQAData.timings).length >
                                0 && (
                                <div>
                                  <div className='text-sm font-medium text-gray-700 mb-2'>
                                    Analysis Time
                                  </div>
                                  <div className='space-y-1'>
                                    {Object.entries(selectedQAData.timings).map(
                                      ([timingName, timing]) => (
                                        <div
                                          key={timingName}
                                          className='flex justify-between text-sm hover:bg-gray-200 hover:font-bold'
                                        >
                                          <span className='text-gray-600 capitalize'>
                                            {timingName.replaceAll('_', ' ')}:
                                          </span>
                                          <span className='text-gray-900 font-medium'>
                                            {formatTime(
                                              timing.duration_ms
                                                ? timing.duration_ms / 1000
                                                : 0
                                            )}
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {hasValidQAData(selectedQAData) &&
                selectedQAData.status === QA_STATUS.COMPLETED && (
                  <div className='space-y-4'>
                    {/* QA Corrections */}
                    {getCorrectionsCount(selectedQAData) > 0 && (
                      <div className='space-y-4'>
                        <h4 className='font-medium text-sm'>
                          QA Corrections ({getCorrectionsCount(selectedQAData)})
                        </h4>
                        <div className='space-y-4'>
                          {selectedQAData.qa_corrections_by_rule?.map(
                            (
                              correction: Record<string, unknown>,
                              index: number
                            ) => (
                              <div
                                key={index}
                                className='p-4 border rounded-lg shadow-sm bg-white'
                              >
                                {/* Three-column layout */}
                                <div className='grid grid-cols-3 gap-4'>
                                  {/* Original Entry Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      Original Entry
                                    </div>
                                    <div className='p-3 border rounded bg-gray-50 min-h-[60px]'>
                                      <div className='text-sm whitespace-pre-wrap'>
                                        {correction.original_entry !== null &&
                                        correction.original_entry !== undefined
                                          ? String(correction.original_entry)
                                          : '-'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* AI Corrected Entry Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      AI Corrected Entry
                                    </div>
                                    <div className='p-3 border rounded-md border-teal-950/20 bg-teal-50 min-h-[60px]'>
                                      <div className='space-y-2'>
                                        <div className='text-sm whitespace-pre-wrap'>
                                          {correction.ai_corrected_entry !==
                                            null &&
                                          correction.ai_corrected_entry !==
                                            undefined
                                            ? String(
                                                correction.ai_corrected_entry
                                              )
                                            : '-'}
                                        </div>
                                        {/* Multiple EMR Corrections */}
                                        {hasPermission('debug:access') &&
                                          expertViewEnabled &&
                                          correction.emr_corrections &&
                                          Array.isArray(
                                            correction.emr_corrections
                                          ) &&
                                          (
                                            correction.emr_corrections as Array<
                                              Record<string, unknown>
                                            >
                                          ).map(
                                            (
                                              emrCorrection: Record<
                                                string,
                                                unknown
                                              >,
                                              emrIndex: number
                                            ) => (
                                              <div
                                                key={emrIndex}
                                                className='p-2 bg-teal-900/20 rounded mt-2'
                                              >
                                                <div className='text-xs font-semibold text-gray-600 mb-1'>
                                                  EMR Correction #{emrIndex + 1}
                                                  :
                                                </div>
                                                {emrCorrection.ai_corrected_answer_id && (
                                                  <div className='flex gap-2 items-start mb-1'>
                                                    <span className='text-xs font-semibold text-gray-600'>
                                                      Answer ID:
                                                    </span>
                                                    <span className='text-xs font-medium font-mono'>
                                                      {String(
                                                        emrCorrection.ai_corrected_answer_id
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                                {emrCorrection.ai_corrected_answer_value !==
                                                  null &&
                                                  emrCorrection.ai_corrected_answer_value !==
                                                    undefined && (
                                                    <div className='flex gap-2 items-start'>
                                                      <span className='text-xs font-semibold text-gray-600'>
                                                        Answer Value:
                                                      </span>
                                                      <span className='text-xs font-medium'>
                                                        {String(
                                                          emrCorrection.ai_corrected_answer_value
                                                        )}
                                                      </span>
                                                    </div>
                                                  )}
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Justification & Source Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      Justification & Source
                                    </div>
                                    <div>
                                      <div className='text-sm mb-3'>
                                        {correction.justification ? (
                                          <ClickableTextWithModals
                                            text={String(
                                              correction.justification
                                            )}
                                            onOpenModal={dataType => {
                                              const medData =
                                                selectedQAData.medical_data as Record<
                                                  string,
                                                  unknown
                                                >;
                                              if (medData) {
                                                handleOpenModal(
                                                  dataType,
                                                  medData[dataType]
                                                );
                                              }
                                            }}
                                            size='sm'
                                          />
                                        ) : (
                                          '-'
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <Separator className='my-4' />

                                {/* Header: Title with severity badge and question ID */}
                                <div className='flex items-start justify-between'>
                                  <div className='flex-1'>
                                    <div className='flex items-center justify-between gap-3 mb-2'>
                                      <h5
                                        className='text-xs text-gray-500'
                                        copyab
                                      >
                                        {(correction.rule_name as string) ||
                                          'Correction'}
                                      </h5>
                                    </div>
                                    {correction.question_id && (
                                      <Badge
                                        variant='outline'
                                        className='text-xs text-cyan-600 border-cyan-600'
                                      >
                                        {correction.question_id as string}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {getCorrectionsCount(selectedQAData) === 0 && (
                      <div className='text-center py-8 text-gray-500'>
                        <CheckCircle className='w-12 h-12 mx-auto mb-2 text-green-500' />
                        <p className='font-medium'>No Issues Found</p>
                        <p className='text-sm'>
                          The visit form answers look good!
                        </p>
                      </div>
                    )}
                  </div>
                )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Medical Data Modal */}
      {modalData && selectedQAData && (
        <MedicalDataModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          title={modalData.title}
          dataType={modalData.dataType}
          data={modalData.data}
          onOpenNestedModal={dataType => {
            const medData = selectedQAData.medical_data as Record<
              string,
              unknown
            >;
            if (medData) {
              handleOpenModal(dataType, medData[dataType]);
            }
          }}
        />
      )}
    </>
  );
};

export default QAVisitDetails;
